
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Activity } from "./ActivityTypes";
import { messageBus } from "@/utils/a2a/communication/messageBus";
import { fetchSuggestedActivities } from "./services/activityService";
import { publishA2AMessage } from "./utils/a2aActivityMessaging";
import { useActivityMutations } from "./hooks/useActivityMutations";
import { useActivityTabsAndRefresh } from "./hooks/useActivityTabsAndRefresh";

export const useSuggestedActivities = () => {
  const queryClient = useQueryClient();

  const {
    data: todayActivities = [],
    isLoading: isLoadingToday,
    error: errorToday,
    refetch: refetchToday,
    isFetching: isFetchingToday,
  } = useQuery<Activity[], Error>({ // Explicitly type useQuery
    queryKey: ["suggested-activities"],
    queryFn: () => fetchSuggestedActivities(), // Fetches for today by default
  });

  const {
    targetTab,
    setTargetTab,
    lastRefresh,
    setLastRefresh,
    tomorrowActivities,
    setTomorrowActivities,
    loadingTomorrow,
    handleRefresh,
    handlePlanForTomorrow,
  } = useActivityTabsAndRefresh({ refetchTodayActivities: refetchToday });
  
  const {
    startActivityMutation,
    completeActivityMutation,
  } = useActivityMutations(targetTab, tomorrowActivities, setTomorrowActivities);


  useEffect(() => {
    const subscription = messageBus.subscribe({
      agentId: '*', // Listen to all agents
      messageTypes: ['NOTIFICATION'], // Specific message types
      filter: (message) =>
        message.metadata?.intent?.startsWith('data-') ||
        message.metadata?.intent?.startsWith('activity-') ||
        message.fromAgent === 'activities-service',
      callback: (message) => {
        console.log('Activity service related message received:', message.content, message.metadata);
        if (message.metadata?.intent === 'data-changed' && message.fromAgent !== 'activities-service') {
          console.log('External activities data changed, refreshing...');
          queryClient.invalidateQueries({ queryKey: ["suggested-activities"] });
          setLastRefresh(new Date());
        }
      }
    });

    return () => {
      messageBus.unsubscribe(subscription.id);
    };
  }, [queryClient, setLastRefresh]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (targetTab === "today") {
        queryClient.invalidateQueries({ queryKey: ["suggested-activities"] });
        setLastRefresh(new Date());
        publishA2AMessage('data-refresh', "Automatic refresh of today's activities");
      }
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(intervalId);
  }, [queryClient, targetTab, setLastRefresh]);


  const displayedActivities = targetTab === "today" ? todayActivities : tomorrowActivities || [];

  return {
    activities: todayActivities, // Today's activities from useQuery
    displayedActivities,
    isLoading: isLoadingToday,
    isFetching: isFetchingToday,
    error: errorToday,
    refetch: refetchToday, // refetch function for today's activities query
    startActivity: startActivityMutation, // Pass the whole mutation object
    completeActivity: completeActivityMutation, // Pass the whole mutation object
    targetTab,
    setTargetTab,
    lastRefresh,
    handleRefresh,
    handlePlanForTomorrow,
    loadingTomorrow,
  };
};
